/**
 * grabDeadzone — the Dead Zone for Head Follow and Eye Follow.
 *
 * The dead zone exists to ignore small accidental nudges when someone STARTS
 * dragging the camera. It is not a jitter filter for every frame:
 *
 *   • only while the user is dragging the camera (cameraInteraction.held);
 *   • only at the START of each drag — the target is held where it was when the
 *     drag began until the camera direction has moved `band` away from there;
 *   • after that, for the rest of the drag, the target passes straight through:
 *     continuous, smooth following, even if the drag pauses;
 *   • never for any other camera motion (scripted moves, camera nodes, momentum
 *     after release, framing, the camera following a walking character).
 *
 * While held, the consumer keeps easing toward the DRAG-START target — so a head
 * still catching up from the previous move finishes that move instead of
 * freezing mid-turn when the next drag begins.
 *
 * Pure, allocation-free, THREE-free (unit-tested under node --test). Angles are
 * a WORLD yaw (atan2(x, z)) and pitch (asin(y)) of the direction to the camera.
 */

import { normalizeAngle } from './headAim.js'

/** One gate per consumer (head, eyes). */
export function createGrabGate() {
  return { grabId: -1, locked: false, yaw: 0, pitch: 0 }
}

/**
 * @param {{grabId:number,locked:boolean,yaw:number,pitch:number}} g  gate state (mutated)
 * @param {boolean} held     user is dragging the camera
 * @param {number} grabId    cameraInteraction.grabId — changes on every new press
 * @param {number} yaw       target yaw this frame (rad, world)
 * @param {number} pitch     target pitch this frame (rad)
 * @param {number} band      dead zone (rad); 0 disables
 * @param {{yaw:number,pitch:number}} out  gated target (written)
 * @returns {{yaw:number,pitch:number}} out
 */
export function stepGrabDeadzone(g, held, grabId, yaw, pitch, band, out) {
  if (!held) {
    g.locked = false
    out.yaw = yaw
    out.pitch = pitch
    return out
  }
  if (grabId !== g.grabId) {
    // A new drag: hold the target where it is until the camera has moved past
    // the dead zone.
    g.grabId = grabId
    g.locked = band > 0
    g.yaw = yaw
    g.pitch = pitch
  }
  if (g.locked && (Math.abs(normalizeAngle(yaw - g.yaw)) > band || Math.abs(pitch - g.pitch) > band)) {
    g.locked = false // past the dead zone — free for the rest of this drag
  }
  out.yaw = g.locked ? g.yaw : yaw
  out.pitch = g.locked ? g.pitch : pitch
  return out
}
